import React, { useState, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Button, IconButton, Chip, Avatar,
    Alert, LinearProgress, Divider, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { LOYALTY_API_BASE } from "../constants";
import { loyaltyFetch } from "../hooks/loyaltyFetch";

// CSV column templates
const CUSTOMER_COLUMNS = ["profileId", "brand", "gender", "birthdate"];

const CUSTOMER_EXAMPLE = [
    ["profileId", "brand", "gender", "birthdate"],
    ["user_001", "mybrand", "M", "1990-05-14"],
    ["user_002", "mybrand", "F", "1985-11-03"],
];

function toCsvString(rows) {
    return rows.map((r) => r.join(",")).join("\n");
}

function downloadCsv(content, filename) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// ── DropZone ──────────────────────────────────────────────────────────────────
function DropZone({ file, onFile, color }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) onFile(f);
    };

    return (
        <Box
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            sx={{
                border: `2px dashed ${dragging ? color : file ? "#22c55e" : "#cbd5e1"}`,
                borderRadius: 3, p: 3, textAlign: "center", cursor: "pointer",
                bgcolor: dragging ? `${color}08` : file ? "#f0fdf4" : "#f8fafc",
                transition: "all 0.2s",
                "&:hover": { border: `2px dashed ${color}`, bgcolor: `${color}06` },
            }}
        >
            <input ref={inputRef} type="file" accept=".csv" style={{ display: "none" }}
                onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
            {file ? (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                    <InsertDriveFileIcon sx={{ fontSize: 32, color: "#22c55e" }} />
                    <Box textAlign="left">
                        <Typography fontWeight={700} fontSize={13} color="#15803d">{file.name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{(file.size / 1024).toFixed(1)} Ko — Cliquer pour changer</Typography>
                    </Box>
                </Box>
            ) : (
                <>
                    <UploadFileIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
                    <Typography fontWeight={600} fontSize={13} color="#475569">Glissez un fichier CSV ici</Typography>
                    <Typography fontSize={11} color="text.secondary" mt={0.5}>ou cliquez pour parcourir</Typography>
                </>
            )}
        </Box>
    );
}

// ── ResultsPanel ──────────────────────────────────────────────────────────────
function ResultsPanel({ result }) {
    if (!result) return null;
    const hasErrors = result.errors?.length > 0;

    return (
        <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <Chip icon={<CheckCircleOutlineIcon />} label={`Traités : ${result.processed}`} size="small"
                    sx={{ fontWeight: 700, bgcolor: "#f0fdf4", color: "#15803d", "& .MuiChip-icon": { color: "#22c55e" } }} />
                <Chip label={`Créés/mis à jour : ${result.upserted}`} size="small" sx={{ fontWeight: 700, bgcolor: "#eff6ff", color: "#1d4ed8" }} />
                {hasErrors && (
                    <Chip icon={<ErrorOutlineIcon />} label={`Erreurs : ${result.errors?.length}`} size="small"
                        sx={{ fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", "& .MuiChip-icon": { color: "#dc2626" } }} />
                )}
            </Box>

            {/* Error details */}
            {hasErrors && (
                <Box sx={{ borderRadius: 2, border: "1px solid #fecaca", bgcolor: "#fffbfb", p: 1.5, maxHeight: 180, overflowY: "auto" }}>
                    <Typography fontSize={12} fontWeight={700} color="#dc2626" mb={1}>
                        ⚠️ Détail des erreurs
                    </Typography>
                    {result.errors?.map((err, i) => (
                        <Box key={i} sx={{ fontSize: 11, color: "#7f1d1d", mb: 0.5, fontFamily: "monospace" }}>
                            Ligne {err.line}: {err.error}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

// ── Main Dialog ────────────────────────────────────────────────────────────────
const COLOR = "#0ea5e9";
const REQUIRED_COLS = ["profileId"];

const ImportDialog = ({ open, onClose, brandName, onImported }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleClose = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(0);
        onClose();
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        setError(null);
        setProgress(20);

        const formData = new FormData();
        formData.append("file", file);

        try {
            setProgress(50);
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/imports/customers`, {
                method: "POST",
                headers: { "X-Brand": brandName || "" },
                body: formData,
            });
            setProgress(90);
            const data = await res.json();
            if (!res.ok) {
                const detail = data?.detail;
                if (typeof detail === "object") {
                    throw new Error(detail.message || JSON.stringify(detail));
                }
                throw new Error(detail || `Erreur HTTP ${res.status}`);
            }
            setResult(data);
            setProgress(100);
            if (onImported) onImported("customers", data);
        } catch (err) {
            setError(err.message);
            setProgress(0);
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadTemplate = () => {
        downloadCsv(toCsvString(CUSTOMER_EXAMPLE), "template_clients.csv");
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
            {/* Header */}
            <DialogTitle sx={{
                background: `linear-gradient(135deg, ${COLOR}18 0%, transparent 100%)`,
                borderBottom: `2px solid ${COLOR}20`, px: 3, py: 2,
                display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: `${COLOR}18` }}>
                        <UploadFileIcon sx={{ fontSize: 20, color: COLOR }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={800} fontSize={16} color="#1a1a2e">
                            Importer des clients
                        </Typography>

                    </Box>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "20px !important", px: 3, pb: 2 }}>
                {/* Column reference */}
                <Box mb={2} p={1.5} sx={{ borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography fontSize={11} fontWeight={700} color="#475569" textTransform="uppercase" letterSpacing={0.5}>
                            Colonnes CSV attendues
                        </Typography>
                        <Tooltip title="Télécharger un fichier CSV d'exemple">
                            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}
                                sx={{ textTransform: "none", fontSize: 11, color: COLOR, fontWeight: 600 }}>
                                Modèle CSV
                            </Button>
                        </Tooltip>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {CUSTOMER_COLUMNS.map((col) => (
                            <Chip key={col} label={col}
                                size="small"
                                sx={{
                                    fontSize: 11, fontFamily: "monospace", height: 22,
                                    bgcolor: REQUIRED_COLS.includes(col) ? `${COLOR}15` : "#f1f5f9",
                                    color: REQUIRED_COLS.includes(col) ? COLOR : "#64748b",
                                    fontWeight: REQUIRED_COLS.includes(col) ? 700 : 400,
                                    border: REQUIRED_COLS.includes(col) ? `1px solid ${COLOR}40` : "none",
                                }}
                            />
                        ))}
                    </Box>
                    <Typography fontSize={10} color="text.secondary" mt={0.75}>
                        * Les colonnes en <strong style={{ color: COLOR }}>bleu foncé</strong> sont obligatoires
                    </Typography>
                </Box>

                {/* Drop zone */}
                <DropZone file={file} onFile={setFile} color={COLOR} />

                {/* Progress bar */}
                {uploading && (
                    <Box mt={1.5}>
                        <LinearProgress variant="determinate" value={progress}
                            sx={{ borderRadius: 2, height: 6, bgcolor: `${COLOR}18`, "& .MuiLinearProgress-bar": { bgcolor: COLOR } }} />
                        <Typography fontSize={11} color="text.secondary" textAlign="center" mt={0.5}>
                            Import en cours…
                        </Typography>
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Results */}
                <ResultsPanel result={result} />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>
                    Fermer
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!file || uploading || !brandName}
                    startIcon={uploading ? null : <UploadFileIcon />}
                    sx={{
                        textTransform: "none", borderRadius: 2, fontWeight: 700, px: 3, color: "#fff",
                        background: `linear-gradient(135deg, ${COLOR} 0%, ${COLOR}cc 100%)`,
                        boxShadow: `0 4px 12px ${COLOR}30`,
                        "&:hover": { boxShadow: `0 6px 20px ${COLOR}45` },
                        "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                    }}
                >
                    {uploading ? "Import en cours…" : "Importer les clients"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportDialog;
